/* eslint-disable no-console */
import { datadogRum } from '@datadog/browser-rum'
import { isDatadogRumEnabled } from 'config/portal'

/**
 * Tracks Flatfile lifecycle events to Datadog RUM.
 * Only sends non-PII data: outcome, durations, stage, counts.
 *
 * Tracked stages:
 *   flatfile_portal_opened  - user opens the upload modal
 *   flatfile_file_uploaded  - user uploads a file (includes portalToUploadMs)
 *   flatfile_submission     - job completes (includes submissionDurationMs,
 *                             uploadToSubmitMs, totalSessionMs)
 *   flatfile_submission_error - any caught error during processing
 */
export class FlatfileDatadogTracker {
  portalOpenTime = null
  fileUploadTime = null
  submissionStartTime = null

  constructor() {
    this.isEnabled = isDatadogRumEnabled()
  }

  _env() {
    return process.env.REACT_APP_DATADOG_RUM_ENV || process.env.NODE_ENV
  }

  /**
   * Called when the Flatfile portal is opened.
   * Resets all timing state so a fresh session is measured.
   */
  startPortalTracking() {
    if (!this.isEnabled) return
    this.portalOpenTime = performance.now()
    this.fileUploadTime = null
    this.submissionStartTime = null

    try {
      datadogRum.addAction('flatfile_portal_opened', {
        stage: 'portal_open',
        env: this._env(),
        service: 'rsp-frontend',
      })
    } catch (e) {
      console.warn(`Failed to track Flatfile portal open: ${e}`)
    }
  }

  /**
   * Called when a file upload completes inside Flatfile.
   * Emits portalToUploadMs so you can see how long it took
   * the user to select and upload their file.
   * @param {Object} [metadata]
   * @param {string} [metadata.recordType]
   */
  trackFileUpload(metadata = {}) {
    if (!this.isEnabled) return
    this.fileUploadTime = performance.now()

    const portalToUploadMs =
      this.portalOpenTime !== null
        ? Math.round(this.fileUploadTime - this.portalOpenTime)
        : null

    try {
      datadogRum.addAction('flatfile_file_uploaded', {
        stage: 'file_upload',
        ...(portalToUploadMs !== null && { portalToUploadMs }),
        env: this._env(),
        service: 'rsp-frontend',
        ...(metadata.recordType && { recordType: metadata.recordType }),
      })
    } catch (e) {
      console.warn(`Failed to track Flatfile file upload: ${e}`)
    }
  }

  /**
   * Called when the Flatfile submit job starts (job:ready fires).
   */
  startSubmissionTracking() {
    if (!this.isEnabled) return
    this.submissionStartTime = performance.now()
  }

  /**
   * Called when Flatfile submission completes.
   * @param {string} outcome - 'success', 'partial', or 'failure'
   * @param {Object} [metadata]
   * @param {number} [metadata.recordCount]
   * @param {number} [metadata.validRecordCount]
   * @param {number} [metadata.invalidRecordCount]
   * @param {string} [metadata.fileType]
   * @param {string} [metadata.recordType]
   */
  completeSubmissionTracking(outcome, metadata = {}) {
    if (!this.isEnabled) return
    if (this.submissionStartTime === null) return

    const now = performance.now()
    const submissionDurationMs = Math.round(now - this.submissionStartTime)
    const uploadToSubmitMs =
      this.fileUploadTime !== null
        ? Math.round(this.submissionStartTime - this.fileUploadTime)
        : null
    const totalSessionMs =
      this.portalOpenTime !== null
        ? Math.round(now - this.portalOpenTime)
        : null

    try {
      datadogRum.addAction('flatfile_submission', {
        stage: 'submission',
        outcome,
        submissionDurationMs,
        ...(uploadToSubmitMs !== null && { uploadToSubmitMs }),
        ...(totalSessionMs !== null && { totalSessionMs }),
        env: this._env(),
        service: 'rsp-frontend',
        // Only include safe metadata (no PII, row data, or file content)
        ...(metadata.recordCount !== undefined && {
          recordCount: metadata.recordCount,
        }),
        ...(metadata.validRecordCount !== undefined && {
          validRecordCount: metadata.validRecordCount,
        }),
        ...(metadata.invalidRecordCount !== undefined && {
          invalidRecordCount: metadata.invalidRecordCount,
        }),
        ...(metadata.fileType && { fileType: metadata.fileType }),
        ...(metadata.recordType && { recordType: metadata.recordType }),
      })
    } catch (e) {
      console.warn(`Failed to track Flatfile submission: ${e}`)
    }

    this.portalOpenTime = null
    this.fileUploadTime = null
    this.submissionStartTime = null
  }

  /**
   * Called if an error is caught during Flatfile processing.
   * @param {string} errorType - e.g. 'api_error', 'validation', 'network'
   * @param {string} [stage] - stage where the error occurred
   */
  trackSubmissionError(errorType, stage = 'submission') {
    if (!this.isEnabled) return

    try {
      datadogRum.addAction('flatfile_submission_error', {
        stage,
        outcome: 'error',
        errorType,
        env: this._env(),
        service: 'rsp-frontend',
      })
    } catch (e) {
      console.warn(`Failed to track Flatfile error: ${e}`)
    }
  }
}

export default new FlatfileDatadogTracker()
